import { RELEASE_STATUS, ReleaseData, ReleaseNote } from "../../types";

export type NotesBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] };

const BULLET = /^\s*[-*•]\s+(.*)$/;

// Plain text only: lines starting with "-", "*" or "•" become list items, other
// non-empty lines become paragraphs. Nothing is interpreted as HTML or markdown.
export const parseNotes = (notes: string): NotesBlock[] => {
  const blocks: NotesBlock[] = [];
  for (const line of notes.split(/\r?\n/)) {
    const bullet = BULLET.exec(line);
    if (bullet) {
      const text = bullet[1].trim();
      if (!text) continue;
      const last = blocks[blocks.length - 1];
      if (last?.kind === "list") last.items.push(text);
      else blocks.push({ kind: "list", items: [text] });
    } else if (line.trim()) {
      blocks.push({ kind: "paragraph", text: line.trim() });
    }
  }
  return blocks;
};

// Tagged releases with non-blank notes, newest first.
export const releasesWithNotes = (releases: ReleaseData[]): ReleaseData[] =>
  releases
    .filter((r) => r.status === RELEASE_STATUS.TAGGED && !!r.publicNotes?.trim())
    .sort((a, b) => (b.taggedAt ?? b.createdAt).localeCompare(a.taggedAt ?? a.createdAt));

export type NotesLoad =
  | { status: "ready"; notes: ReleaseNote[] }
  | { status: "empty" }
  | { status: "error" };

// One fetch, mapped to a view state; a thrown or unsuccessful response is "error".
export const loadReleaseNotes = async (
  fetchNotes: () => Promise<{ success: boolean; data?: { notes: ReleaseNote[] } }>,
): Promise<NotesLoad> => {
  try {
    const res = await fetchNotes();
    const all = res?.success ? res.data?.notes : undefined;
    if (!Array.isArray(all)) return { status: "error" };
    const notes = all.filter((n) => !!n.publicNotes?.trim());
    return notes.length ? { status: "ready", notes } : { status: "empty" };
  } catch {
    return { status: "error" };
  }
};

// Session cache: notes only change on a release, so a successful load is kept
// for the page's lifetime. Errors are never cached, so reopening retries.
// Concurrent callers share one in-flight fetch.
let cachedNotes: NotesLoad | null = null;
let pending: Promise<NotesLoad> | null = null;

export const getCachedReleaseNotes = (): NotesLoad | null => cachedNotes;

export const loadReleaseNotesCached = (
  fetchNotes: Parameters<typeof loadReleaseNotes>[0],
): Promise<NotesLoad> => {
  if (cachedNotes) return Promise.resolve(cachedNotes);
  if (pending) return pending;
  const request: Promise<NotesLoad> = loadReleaseNotes(fetchNotes).then((result) => {
    if (pending === request) {
      pending = null;
      if (result.status !== "error") cachedNotes = result;
    }
    return result;
  });
  pending = request;
  return request;
};

export const clearReleaseNotesCache = () => {
  cachedNotes = null;
  pending = null;
};
